/**
 * Pure tree operations over a document's block list.
 *
 * A block's children are `Block[][]` — one array per named slot, positionally
 * matched to the component manifest's `@slot` declarations (see `manifest.ts`).
 * So a location is a triple: `(parentId, slot, index)`, with `parentId: null`
 * and `slot: 0` meaning the document root.
 *
 * Everything here is immutable and copies only along the touched path, so
 * untouched subtrees keep their identity. That matters: `compile()` is memoized
 * per block, and the canvas re-measures overlay rects from the compiled output —
 * a reducer that rebuilt the whole tree on every keystroke would invalidate both.
 */
import { Block, nextBlockId } from './document';

export interface BlockLocation {
  /** null = document root. */
  parentId: string | null;
  /** Index into the parent's slot array; 0 at the root. */
  slot: number;
  /** Position within that slot. */
  index: number;
}

/** A block's slots, normalized — a block with no children has none. */
export function slotsOf(block: Block): Block[][] {
  return block.children ?? [];
}

export function findBlock(blocks: Block[], id: string): Block | null {
  for (const block of blocks) {
    if (block.id === id) return block;
    for (const slot of slotsOf(block)) {
      const found = findBlock(slot, id);
      if (found) return found;
    }
  }
  return null;
}

/** Where a block sits, or null when it isn't in the tree. */
export function findLocation(
  blocks: Block[],
  id: string,
): BlockLocation | null {
  return locate(blocks, id, null, 0);
}

function locate(
  blocks: Block[],
  id: string,
  parentId: string | null,
  slot: number,
): BlockLocation | null {
  const index = blocks.findIndex((block) => block.id === id);
  if (index !== -1) return { parentId, slot, index };

  for (const block of blocks) {
    const slots = slotsOf(block);
    for (let childSlot = 0; childSlot < slots.length; childSlot += 1) {
      const hit = locate(slots[childSlot], id, block.id, childSlot);
      if (hit) return hit;
    }
  }
  return null;
}

/** Every id in a subtree, the block itself included. */
export function collectIds(block: Block, into: string[] = []): string[] {
  into.push(block.id);
  for (const slot of slotsOf(block)) {
    for (const child of slot) collectIds(child, into);
  }
  return into;
}

/**
 * True when `candidateId` sits anywhere beneath `ancestorId`.
 *
 * Guards the move operation: dropping a section into its own child would
 * detach the subtree from the document and — because the compiler walks
 * children recursively — hang on the resulting cycle.
 */
export function isDescendant(
  blocks: Block[],
  ancestorId: string,
  candidateId: string,
): boolean {
  const ancestor = findBlock(blocks, ancestorId);
  if (!ancestor) return false;
  return collectIds(ancestor).includes(candidateId) && ancestorId !== candidateId;
}

/** Copy of `slots` with empty arrays appended so `slot` is addressable. */
function padSlots(slots: Block[][], slot: number): Block[][] {
  if (slot < slots.length) return [...slots];
  const padded = [...slots];
  while (padded.length <= slot) padded.push([]);
  return padded;
}

/**
 * Rebuilds only the branch containing `parentId`, applying `apply` to its slot.
 *
 * Returns the *identical* array when nothing below matched. `Array.prototype.map`
 * always allocates, so without that check every level would report a change and
 * the whole tree would be rebuilt on every insert — defeating the memoized
 * per-block compile and forcing a full overlay re-measure mid-drag.
 */
function mapSlot(
  blocks: Block[],
  parentId: string,
  slot: number,
  apply: (children: Block[]) => Block[],
): Block[] {
  let changed = false;
  const next = blocks.map((block) => {
    if (block.id === parentId) {
      changed = true;
      const slots = padSlots(slotsOf(block), slot);
      slots[slot] = apply(slots[slot]);
      return { ...block, children: slots };
    }

    const slots = slotsOf(block);
    if (slots.length === 0) return block;

    let slotChanged = false;
    const nextSlots = slots.map((children) => {
      const inner = mapSlot(children, parentId, slot, apply);
      if (inner !== children) slotChanged = true;
      return inner;
    });
    if (slotChanged) {
      changed = true;
      return { ...block, children: nextSlots };
    }
    return block;
  });
  return changed ? next : blocks;
}

function insertAt(children: Block[], block: Block, index: number): Block[] {
  const at = Math.max(0, Math.min(index, children.length));
  return [...children.slice(0, at), block, ...children.slice(at)];
}

export function insertBlock(
  blocks: Block[],
  block: Block,
  location: BlockLocation,
): Block[] {
  if (location.parentId === null) {
    return insertAt(blocks, block, location.index);
  }
  return mapSlot(blocks, location.parentId, location.slot, (children) =>
    insertAt(children, block, location.index),
  );
}

export interface RemoveResult {
  blocks: Block[];
  /** The detached subtree, or null when the id wasn't present. */
  removed: Block | null;
}

export function removeBlock(blocks: Block[], id: string): RemoveResult {
  const index = blocks.findIndex((block) => block.id === id);
  if (index !== -1) {
    return {
      blocks: [...blocks.slice(0, index), ...blocks.slice(index + 1)],
      removed: blocks[index],
    };
  }

  let removed: Block | null = null;
  const next = blocks.map((block) => {
    if (removed) return block;
    const slots = slotsOf(block);
    if (slots.length === 0) return block;

    let changed = false;
    const nextSlots = slots.map((children) => {
      if (removed) return children;
      const result = removeBlock(children, id);
      if (result.removed) {
        removed = result.removed;
        changed = true;
        return result.blocks;
      }
      return children;
    });
    return changed ? { ...block, children: nextSlots } : block;
  });

  return { blocks: removed ? next : blocks, removed };
}

/** Applies `updater` to one block, leaving every other branch identical. */
export function updateBlock(
  blocks: Block[],
  id: string,
  updater: (block: Block) => Block,
): Block[] {
  let changed = false;
  const next = blocks.map((block) => {
    if (block.id === id) {
      changed = true;
      return updater(block);
    }

    const slots = slotsOf(block);
    if (slots.length === 0) return block;

    let slotChanged = false;
    const nextSlots = slots.map((children) => {
      const inner = updateBlock(children, id, updater);
      if (inner !== children) slotChanged = true;
      return inner;
    });
    if (slotChanged) {
      changed = true;
      return { ...block, children: nextSlots };
    }
    return block;
  });
  return changed ? next : blocks;
}

/**
 * A deep copy with fresh ids throughout.
 *
 * Duplication has to re-id the whole subtree: ids are emitted as `data-cvn-id`,
 * which the canvas overlay uses to map a rect back to a block. Two blocks
 * sharing an id makes selection ambiguous and hit-testing wrong.
 */
export function regenerateIds(block: Block): Block {
  const copy: Block = { ...block, id: nextBlockId() };
  const slots = slotsOf(block);
  if (slots.length > 0) {
    copy.children = slots.map((children) => children.map(regenerateIds));
  }
  return copy;
}

/**
 * Moves a block to a new location.
 *
 * Returns the blocks unchanged when the move is impossible (unknown id) or
 * illegal (into its own descendant). Within the same slot, the target index is
 * interpreted against the list *before* removal — which is what a drag gesture
 * means — so it is decremented after removal when moving further back.
 */
export function moveBlock(
  blocks: Block[],
  id: string,
  target: BlockLocation,
): Block[] {
  const from = findLocation(blocks, id);
  if (!from) return blocks;
  if (target.parentId !== null && target.parentId === id) return blocks;
  if (target.parentId !== null && isDescendant(blocks, id, target.parentId)) {
    return blocks;
  }

  const sameSlot =
    from.parentId === target.parentId && from.slot === target.slot;
  if (sameSlot && from.index === target.index) return blocks;

  const { blocks: without, removed } = removeBlock(blocks, id);
  if (!removed) return blocks;

  const index =
    sameSlot && target.index > from.index ? target.index - 1 : target.index;

  return insertBlock(without, removed, { ...target, index });
}

/**
 * Gives a moved top-level block the layout region of its new neighbours.
 *
 * Regions are *the* thing a top-level drag changes on a layouted email: the canvas shows
 * `header`, `intro_content`, `footer` and the rest as separate bands, so dragging a block
 * from one band into another obviously means "put it in that region". Without this the
 * move reorders the document but leaves `layoutSlot` alone, so the block recompiles back
 * into its old region and appears to snap back — which is why the region had to be picked
 * from a dropdown instead.
 *
 * **The preceding sibling wins**, falling back to the following one. Content flows
 * downward, so landing *below* something reads as joining it; at the very top there is
 * nothing above to join, so the block below decides.
 *
 * Only for the root level: a nested block lives inside a parent that is already in a
 * region, and a second region on it would be a contradiction the compiler could not
 * honour.
 */
export function adoptLayoutSlot(blocks: Block[], blockId: string): Block[] {
  const index = blocks.findIndex((block) => block.id === blockId);
  if (index === -1) return blocks;

  const neighbour = blocks[index - 1] ?? blocks[index + 1];
  const slot = neighbour?.layoutSlot;
  if (!slot || blocks[index].layoutSlot === slot) return blocks;

  const next = [...blocks];
  next[index] = { ...next[index], layoutSlot: slot };
  return next;
}
